import React from "react";
import { Box } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  onPrev?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  prevDisabled?: boolean;
};

/**
 * Presentational sticky bottom bar (blue) with a text "Previous" + brown "Next"
 * pill. Used by screens outside the main quiz stepper (e.g. the profile summary)
 * that need the same chrome but their own navigation handlers.
 */
export function QuizNavBar({ onPrev, onNext, nextLabel = "Next", prevDisabled }: Props) {
  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100,
        height: `calc(${QUIZ.layout.bottomBarMobile}px + env(safe-area-inset-bottom))`,
        pb: "env(safe-area-inset-bottom)",
        background: QUIZ.color.blue,
        boxShadow: "0 -4px 17px rgba(0,0,0,0.14)",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", md: 667 },
          mx: "auto",
          px: { xs: "30px", md: "32px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
        }}
      >
        <Box
          role="button"
          onClick={prevDisabled ? undefined : onPrev}
          sx={{
            fontFamily: QUIZ.font.body,
            fontWeight: 500,
            fontSize: { xs: "14.78px", md: "16px" },
            color: QUIZ.color.brown,
            cursor: prevDisabled ? "default" : "pointer",
            opacity: prevDisabled ? 0.4 : 1,
            px: "12px",
            py: "6px",
            userSelect: "none",
            "&:hover": { opacity: prevDisabled ? 0.4 : 0.7 },
          }}
        >
          Previous
        </Box>
        <Box
          role="button"
          onClick={onNext}
          sx={{
            bgcolor: QUIZ.color.brown,
            color: QUIZ.color.white,
            fontFamily: QUIZ.font.button,
            fontWeight: 700,
            fontSize: "18px",
            lineHeight: 1.5,
            borderRadius: QUIZ.radius.pill,
            px: "46px",
            py: "12px",
            cursor: "pointer",
            userSelect: "none",
            whiteSpace: "nowrap",
            boxShadow: "0px 4px 3px rgba(0,0,0,0.1), 0px 2px 2px rgba(0,0,0,0.1)",
            "&:hover": { opacity: 0.9 },
          }}
        >
          {nextLabel}
        </Box>
      </Box>
    </Box>
  );
}

export default QuizNavBar;

export function QuizInlineCTA({ onClick, label = "Next" }: { onClick?: () => void; label?: string }) {
  return (
    <Box
      role="button"
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: QUIZ.color.blue,
        color: QUIZ.color.ink, 
        fontFamily: QUIZ.font.button,
        fontWeight: 700,
        fontSize: "18px",
        lineHeight: 1.5,
        borderRadius: QUIZ.radius.pill,
        px: "46px",
        py: "12px",
        width: "100%",
        cursor: "pointer",
        userSelect: "none",
        boxShadow: "0px 4px 3px rgba(0,0,0,0.1), 0px 2px 2px rgba(0,0,0,0.1)",
        "&:hover": { opacity: 0.9 },
      }}
    >
      {label}
    </Box>
  );
}
